"use client";

import cx from "classnames";
import { every } from "lodash-es";
import ErrorComponent from "@/components/empty/ErrorComponent";
import { Tab, Tabs } from "@/components/headToHead";
import { Card, CardHeader, CtaLink } from "@/components/headToHead/Card";
import LoadingBackdrop from "@/components/loadingBackdrop/LoadingBackdrop";
import ErrorVan from "@/components/svg/ErrorVan";
import { SPORTSBOOK_URL } from "@/constants/amplitude";
import useGetHeadToHeadPlayerStats from "@/utils/hooks/useGetHeadToHeadPlayerStats";
import { useHeadToHeadContext } from "@/utils/hooks/useHeadToHeadContext";
import { TabContextProvider } from "@/utils/hooks/useTabsContext";
import { LineBetMarkets, TotalBetMarkets } from "./Markets";
import OddsUnavailable from "./oddsUnavailable";
import styles from "./PlayerProps.module.scss";

enum TabLabel {
  TD_SCORER = "TD Scorer Props",
  PASSING = "Passing Props",
  RECEIVING = "Receiving Props",
  RUSHING = "Rushing Props",
}

enum TabValue {
  TD_SCORER = "td-scorer",
  PASSING = "passing",
  RECEIVING = "receiving",
  RUSHING = "rushing",
}

const TAB = {
  [TabLabel.TD_SCORER]: {
    label: TabLabel.TD_SCORER,
    value: TabValue.TD_SCORER,
  },
  [TabLabel.PASSING]: {
    label: TabLabel.PASSING,
    value: TabValue.PASSING,
  },
  [TabLabel.RECEIVING]: {
    label: TabLabel.RECEIVING,
    value: TabValue.RECEIVING,
  },
  [TabLabel.RUSHING]: {
    label: TabLabel.RUSHING,
    value: TabValue.RUSHING,
  },
};

const TABS = [
  TAB[TabLabel.TD_SCORER],
  TAB[TabLabel.PASSING],
  TAB[TabLabel.RECEIVING],
  TAB[TabLabel.RUSHING],
];

const SELECTION_TO_MARKETS = {
  [TabLabel.TD_SCORER]: ["Any Time Touchdown Scorer", "First Touchdown Scorer"],
  [TabLabel.PASSING]: ["Player Passing Yards", "Player Passing TDs"],
  [TabLabel.RECEIVING]: ["Player Receiving Yards", "Player Total Receptions"],
  [TabLabel.RUSHING]: ["Player Rushing Yards", "Player Rush Attempts"],
};

const PlayerProps = () => {
  const ampBaseUrl = SPORTSBOOK_URL; // temporary until we have a proper URL
  const { gameId, awayTeam, homeTeam } = useHeadToHeadContext();
  const awayTeamShortName = awayTeam?.shortName ?? "";
  const homeTeamShortName = homeTeam?.shortName ?? "";

  const { data, isLoading, isError } = useGetHeadToHeadPlayerStats(gameId);
  const odds = data?.event?.playerPropOdds ?? {};
  const anyTimeTouchdownScorerOdds = odds?.anyTimeTouchdownScorer ?? [];
  const firstTouchdownScorerOdds = odds?.firstTouchdownScorer ?? [];
  const playerPassingYardsOdds = odds?.totalPassingYards ?? [];
  const playerPassingTDsOdds = odds?.totalPassingTDs ?? [];
  const playerReceivingYardsOdds = odds?.totalReceivingYards ?? [];
  const playerReceptionsOdds = odds?.totalReceptions ?? [];
  const playerRushingYardsOdds = odds?.totalRushingYards ?? [];
  const playerRushAttemptsOdds = odds?.totalRushAttempts ?? [];
  const emptyPlayerProps = every([
    anyTimeTouchdownScorerOdds.length === 0,
    anyTimeTouchdownScorerOdds.length === 0,
    firstTouchdownScorerOdds.length === 0,
    playerPassingYardsOdds.length === 0,
    playerPassingTDsOdds.length === 0,
    playerReceivingYardsOdds.length === 0,
    playerReceptionsOdds.length === 0,
    playerRushingYardsOdds.length === 0,
    playerRushAttemptsOdds.length === 0,
  ]);

  const cardContentClasses = cx(styles.cardContent, {
    [styles.empty]: isLoading || isError || emptyPlayerProps,
  });

  return (
    <Card>
      <CardHeader
        title={`${awayTeamShortName} @ ${homeTeamShortName} Odds`}
        action={
          <CtaLink
            next={false}
            href={ampBaseUrl}
            label="View more NFL odds in Sportsbook"
            ampModule="player_props"
          />
        }
      />
      <TabContextProvider defaultTab={TabValue.TD_SCORER}>
        <div className={styles.scrollContainer}>
          <Tabs tabs={TABS} />
        </div>
        <div className={cardContentClasses}>
          <LoadingBackdrop loading={isLoading} />
          {isError ? (
            <ErrorComponent
              image={<ErrorVan width={172} height={138} />}
              headline="Aaand... no good!"
              showCta={false}
              subtext={
                <>
                  <p>No player props available</p>
                  <p>Please check back later.</p>
                </>
              }
            />
          ) : null}
          {!isLoading && !isError && emptyPlayerProps && <OddsUnavailable />}
          {!isLoading && !isError && !emptyPlayerProps && (
            <div>
              <Tab value={TabValue.TD_SCORER}>
                <div className={styles.tabContent}>
                  <LineBetMarkets
                    title={SELECTION_TO_MARKETS[TabLabel.TD_SCORER][0]}
                    odds={anyTimeTouchdownScorerOdds}
                  />
                  <LineBetMarkets
                    title={SELECTION_TO_MARKETS[TabLabel.TD_SCORER][1]}
                    odds={firstTouchdownScorerOdds}
                  />
                </div>
              </Tab>
              <Tab value={TabValue.PASSING}>
                <div className={styles.tabContent}>
                  <TotalBetMarkets
                    title={SELECTION_TO_MARKETS[TabLabel.PASSING][0]}
                    odds={playerPassingYardsOdds}
                  />
                  <TotalBetMarkets
                    title={SELECTION_TO_MARKETS[TabLabel.PASSING][1]}
                    odds={playerPassingTDsOdds}
                  />
                </div>
              </Tab>
              <Tab value={TabValue.RECEIVING}>
                <div className={styles.tabContent}>
                  <TotalBetMarkets
                    title={SELECTION_TO_MARKETS[TabLabel.RECEIVING][0]}
                    odds={playerReceivingYardsOdds}
                  />
                  <TotalBetMarkets
                    title={SELECTION_TO_MARKETS[TabLabel.RECEIVING][1]}
                    odds={playerReceptionsOdds}
                  />
                </div>
              </Tab>
              <Tab value={TabValue.RUSHING}>
                <div className={styles.tabContent}>
                  <TotalBetMarkets
                    title={SELECTION_TO_MARKETS[TabLabel.RUSHING][0]}
                    odds={playerRushingYardsOdds}
                  />
                  <TotalBetMarkets
                    title={SELECTION_TO_MARKETS[TabLabel.RUSHING][1]}
                    odds={playerRushAttemptsOdds}
                  />
                </div>
              </Tab>
            </div>
          )}
        </div>
      </TabContextProvider>
    </Card>
  );
};

export default PlayerProps;
